import { useState } from "react";
import {
  GalleryVerticalEnd,
  GripVertical,
  HelpCircle,
  LayoutGrid,
  MessageSquareQuote,
  MousePointerClick,
  RefreshCw,
  Sparkles,
  Tags,
  TextCursorInput,
} from "lucide-react";
import { Card, Button } from "@/ui";
import type { NaraBlock } from "@/mock";
import { cn } from "@/lib/cn";

const BLOCK_ICON: Record<NaraBlock["type"], typeof LayoutGrid> = {
  hero: GalleryVerticalEnd,
  features: LayoutGrid,
  pricing: Tags,
  cta: MousePointerClick,
  testimonial: MessageSquareQuote,
  faq: HelpCircle,
  form: TextCursorInput,
};

/**
 * A generated block as an editable card. Regenerate / Edit are quiet ghost
 * actions; publishing lives once on the canvas, never per-block.
 */
export function NaraBlockCard({
  block,
  index,
  onRegenerate,
}: {
  block: NaraBlock;
  index: number;
  onRegenerate: (id: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const Icon = BLOCK_ICON[block.type] ?? Sparkles;

  const regenerate = () => {
    setBusy(true);
    onRegenerate(block.id);
    window.setTimeout(() => setBusy(false), 900);
  };

  return (
    <Card
      className="animate-fade-up p-4"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 hidden text-subtle sm:block" aria-hidden>
          <GripVertical className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-btn bg-paper-2 text-ink dark:bg-white/5">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="eyebrow !text-subtle">{block.type}</p>
          </div>
          <h4 className="mt-0.5 text-[14px] font-semibold leading-snug text-ink-strong">
            {block.title}
          </h4>
          <p className="mt-0.5 text-[13px] leading-relaxed text-muted">{block.summary}</p>
          <div className="mt-3 flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={regenerate} disabled={busy}>
              <RefreshCw
                className={cn("h-3.5 w-3.5", busy && "animate-spin")}
                strokeWidth={1.75}
              />
              {busy ? "Regenerating" : "Regenerate"}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
